'use client';

import * as SheetPrimitive from '@radix-ui/react-dialog';
import { Close } from '@shared/icons';
import type * as React from 'react';
import { cn } from '../lib/utils';

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({ ...props }: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  const CHROMA_CLASSES = 'bg-overlay-background/80 backdrop-blur-[8px]';

  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50',
        CHROMA_CLASSES,
        className
      )}
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  side = 'right',
  closeIconSize = 'medium',
  showCloseButton = true,
  title = 'Sheet dialog',
  customOverlayClassName,
  customOverlayStyle,
  closeTrackName = 'Close',
  'data-track-container': dataTrackContainer,
  'data-track-feature': dataTrackFeature,
  dataTestIdBaseName = 'sheet',
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: 'top' | 'right' | 'bottom' | 'left';
  closeIconSize?: React.ComponentProps<typeof Close>['size'];
  /**
   * Whether to show the default close button. Set to false to hide it completely.
   * @default true
   */
  showCloseButton?: boolean;
  /**
   * Accessible title for the dialog. If you are already rendering a visible title using SheetTitle, pass the same text here.
   * Defaults to "Sheet dialog".
   */
  title?: string;
  /**
   * Custom Chroma className for the overlay. Allows overriding the default overlay classes.
   */
  customOverlayClassName?: string;
  /**
   * Custom style property for the overlay
   */
  customOverlayStyle?: React.CSSProperties;
  /**
   * Tracking name for the default close button. Applied to data-track-name.
   * @default "Close"
   */
  closeTrackName?: string;
  'data-track-container'?: string;
  'data-track-feature'?: string;
  dataTestIdBaseName?: string;
}) {
  return (
    <SheetPortal>
      <SheetOverlay className={customOverlayClassName} style={customOverlayStyle} />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        className={cn(
          'bg-container-base data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col shadow-6 transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 overflow-y-scroll hide-scrollbar',
          side === 'right' && 'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 sm:max-w-sm rounded-tl-lg',
          side === 'left' && 'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm',
          side === 'top' && 'data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b',
          side === 'bottom' && 'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto rounded-t-lg',
          className
        )}
        {...(dataTrackContainer && { 'data-track-container': dataTrackContainer })}
        {...(dataTrackFeature && { 'data-track-feature': dataTrackFeature })}
        {...props}
      >
        {/* Visually hidden title to satisfy Radix accessibility warning if consumer forgets to render SheetTitle */}
        <SheetPrimitive.Title className="sr-only">{title}</SheetPrimitive.Title>
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close
            data-track-name={closeTrackName}
            data-testid={`${dataTestIdBaseName}-close-button`}
            className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute z-10 top-[calc(1.5rem+env(safe-area-inset-top,0px))] right-[calc(1.5rem+env(safe-area-inset-right,0px))] rounded-3 opacity-100 transition-opacity hover:opacity-90 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none"
          >
            <Close size={closeIconSize} className="text-foreground" />
            <span className="sr-only" data-testid="close-button">
              Close
            </span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="sheet-header" className={cn('flex flex-col gap-3 pt-6 pb-12 px-6', className)} {...props} />;
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="sheet-footer" className={cn('mt-auto flex flex-col gap-4 p-8', className)} {...props} />;
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return <SheetPrimitive.Title data-slot="sheet-title" className={cn('text-foreground font-semibold', className)} {...props} />;
}

function SheetDescription({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return <SheetPrimitive.Description data-slot="sheet-description" className={cn('text-foreground-subtle text-sm', className)} {...props} />;
}

export { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetPortal, SheetTitle, SheetTrigger };
